import type { SxProps, Theme } from '@mui/material';
import { Typography } from '@mui/material';
import { Box, Chip, Paper } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import type { PropsWithChildren } from 'react';
import React from 'react';

export type InfoCardColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

type InfoCardProps = PropsWithChildren<{
  label?: React.ReactNode;
  color?: InfoCardColor;
  variant?: 'outlined' | 'filled';
}>;

export const VyInfoCard: React.FC<InfoCardProps> = ({ children, label, color = 'primary', variant = 'outlined' }) => {
  const outlinedPaperStyle: SxProps = {
    padding: 2,
    borderRadius: '8px',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: '72px',
  };

  const filledPaperStyle: SxProps<Theme> = (theme: Theme) => ({
    padding: 2,
    borderRadius: '8px',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: '72px',
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  });

  return (
    <Paper
      elevation={1}
      sx={variant === 'outlined' ? outlinedPaperStyle : filledPaperStyle}
    >
      {variant === 'outlined' && (
        <Box
          sx={theme => ({
            position: 'absolute',
            left: 0,
            top: 0,
            background: theme.palette[color].main,
            width: '2px',
            height: '100%',
          })}
        />
      )}

      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height={1}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        >
          {variant === 'outlined' && (
            <Chip
              label={label}
              color={color}
              size="medium"
              variant="outlined"
            />
          )}

          {variant === 'filled' && (
            <Typography
              color={blueGrey[300]}
              variant="subtitle2"
              fontSize="14px"
              fontWeight={400}
              lineHeight="22px"
            >
              {label}
            </Typography>
          )}
        </Box>

        <Box>
          {children}
        </Box>
      </Stack>
    </Paper>
  );
};